import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { CCard, CCardGroup } from "@coreui/react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { useTranslationContext } from "@/context/TranslationContext";
import CmsForm from "../cmsForm";
import { cms } from "@/constants/translation/cms.translation";
import { breadcrumb_key, messages } from "@/constants/translation/common.translation";
import CmsValidationClass from "@/helpers/validations/cms";
import { useAddCms } from "@/hooks/useCms";
import { TranslationService } from "@/api";
import { masters, dashboard } from "@/libs/routes";

const ENGLISH_CODE = "en";

/**
 * Component for adding a new CMS page.
 * @returns {JSX.Element} The rendered AddCms component.
 */
const AddCms = () => {
  const navigate = useNavigate();
  const t = useTranslationContext();
  const queryClient = useQueryClient();

  const { data: languagesResponse, isLoading: isLanguagesLoading } = useQuery({
    queryKey: ["cms-add-languages"],
    queryFn: () => TranslationService.getLanguages(),
    refetchOnWindowFocus: false,
  });

  const { mutate: doAddCms, isPending: isSubmitting } = useAddCms(
    (res) => {
      toast.success(res?.message || t(messages.toast.cms.added));
      queryClient.invalidateQueries({ queryKey: ["cms-list"] });
      navigate(masters.cms.list);
    },
    (err) => {
      toast.error(err?.response?.data?.message || t(messages.toast.cms.addFailed));
    }
  );

  // English first, then the rest alphabetically by name
  const languages = useMemo(() => {
    // API returns either an array or an object keyed "0", "1", ...
    const languagesData = languagesResponse?.data || [];
    const list = Array.isArray(languagesData) ? [...languagesData] : Object.values(languagesData);
    list.sort((a, b) => {
      if (a.code === ENGLISH_CODE) return -1;
      if (b.code === ENGLISH_CODE) return 1;
      return (a.name || "").localeCompare(b.name || "");
    });
    return list;
  }, [languagesResponse]);

  const englishLanguageId = languages.find((l) => l.code === ENGLISH_CODE)?.id;

  const initialValues = useMemo(
    () => ({
      slug: "",
      is_active: "true",
      translations: languages.map((l) => ({
        language_id: l.id,
        language_code: l.code,
        language_name: l.name,
        title: "",
        description: "",
        seo_meta_title: "",
        seo_meta_desc: "",
      })),
    }),
    [languages]
  );

  const validationSchema = useMemo(
    () => CmsValidationClass.cmsValidation(t, englishLanguageId, true),
    [t, englishLanguageId]
  );

  const handleAdd = (values) => {
    // English is always sent; untouched (fully empty) optional languages are omitted
    const payload = values.translations
      .filter(
        (l) =>
          String(l.language_id) === String(englishLanguageId) ||
          l.title.trim() ||
          l.description.trim()
      )
      .map((l) => ({
        language_id: Number(l.language_id),
        title: l.title.trim(),
        description: l.description,
        seo_meta_title: l.seo_meta_title.trim(),
        seo_meta_desc: l.seo_meta_desc.trim(),
      }));
    doAddCms({
      slug: values.slug.trim(),
      is_active: values.is_active === "true",
      translations: payload,
    });
  };

  if (isLanguagesLoading || languages.length === 0) {
    return (
      <CCardGroup>
        <CCard className="p-4">
          <div className="text-center">
            <p>{t(cms.edit.loading)}</p>
          </div>
        </CCard>
      </CCardGroup>
    );
  }

  return (
    <CCardGroup>
      <CCard className="p-4">
        <h1 className="text-center">{t(cms.add.main_heading)}</h1>
        <CmsForm
          initialValues={initialValues}
          onSubmit={handleAdd}
          isSubmitting={isSubmitting}
          validationSchema={validationSchema}
        />
      </CCard>
    </CCardGroup>
  );
};

AddCms.breadCrumbArray = [
  { name: breadcrumb_key.home, pathname: dashboard, active: false },
  {
    name: breadcrumb_key.masters?.cms?.list,
    pathname: masters.cms.list,
    active: false,
  },
  {
    name: breadcrumb_key.masters?.cms?.add,
    pathname: masters.cms.add,
    active: true,
  },
];

export default AddCms;
